import { QueryClient } from '@tanstack/react-query';

/**
 * Utility functions for managing React Query cache
 */
export class QueryUtils {
  /**
   * Clear all cached data from React Query
   * Use this during logout to prevent data leakage between users
   */
  static clearAllCache(queryClient: QueryClient): void {
    queryClient.clear();
  }

  /**
   * Remove all queries from cache
   * Alternative to clear() but keeps observers
   */
  static removeAllQueries(queryClient: QueryClient): void {
    queryClient.removeQueries();
  }

  /**
   * Invalidate all queries
   * This will refetch all active queries
   */
  static invalidateAllQueries(queryClient: QueryClient): void {
    queryClient.invalidateQueries();
  }

  /**
   * Clear cache for specific query patterns
   * Useful for clearing user-specific data
   */
  static clearUserSpecificCache(queryClient: QueryClient, userId?: string | number): void {
    if (userId) {
      // Remove queries that might contain user-specific data
      queryClient.removeQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          // Check if query key contains user-specific data
          return (
            queryKey.includes('process') ||
            queryKey.includes('resource') ||
            queryKey.includes('result') ||
            queryKey.includes('dashboard') ||
            queryKey.includes('user') ||
            queryKey.includes(userId)
          );
        },
      });
    } else {
      // If no userId provided, clear all common user data
      queryClient.removeQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return (
            queryKey.includes('process') ||
            queryKey.includes('resource') ||
            queryKey.includes('result') ||
            queryKey.includes('dashboard')
          );
        },
      });
    }
  }

  /**
   * Clear cache and reset to initial state
   * Most comprehensive cleanup for logout
   */
  static resetCacheForLogout(queryClient: QueryClient): void {
    // Clear all cache
    queryClient.clear();
    
    // Reset query data
    queryClient.resetQueries();
    
    // Cancel any outgoing queries
    queryClient.cancelQueries();
  }
}
